<template>
  <v-container fluid id="settlements-by-contract">
    <v-row>
      <v-col cols="12">
        <v-data-table
        :items="settlements"
        :headers="headers"
        :expanded="expanded"
        item-key="contractInfo.contractId"
        show-expand
        single-expand
        @click:row="rowClicked"
        style="cursor: pointer;"
        dense
        fixed-header
        height="max(256px, calc(100vh - 485px))"
        :items-per-page="getDataTableDefaultItemsPerPage($vuetify.breakpoint)"
        :footer-props="{
          'items-per-page-options': dataTableItemsPerPage
        }">
          <template #item.contractInfo.netTons="{item}">
            {{numberWithCommas(item.contractInfo.netTons, 3)}}
          </template>
          <template #item.contractInfo.grossTons="{item}">
            {{numberWithCommas(item.contractInfo.grossTons, 3)}}
          </template>
          <template #item.contractInfo.netVolume="{item}">
            {{mbfStringFromBf(item.contractInfo.netVolume)}}
          </template>
          <template #item.contractInfo.grossVolume="{item}">
            {{mbfStringFromBf(item.contractInfo.grossVolume)}}
          </template>
          <template #item.contractInfo.grossAmount="{item}">
            {{formatMoney(item.contractInfo.grossAmount)}}
          </template>
          <template #item.contractInfo.amount="{item}">
            <Icon
            v-if="contractHasNegativePayment(item.payeeInfo)"
            icon="mdi-alert"
            iconColor="warning"
            tooltipColor="warning"
            :tooltipText="$t('contractHasNegativePayment')"
            elementId="sbc-negative-payment"/>
            {{formatMoney(item.contractInfo.amount)}}
          </template>
          <template #item.rate="{item}">
            <Icon
            v-if="item.contractInfo.inlineRates.length > 1"
            icon="mdi-dots-horizontal"
            iconColor="black"
            :tooltipText="$t('multipleRatesForContract')"
            @icon-clicked="rateVerificationClicked(item)"
            elementId="slrv"/>
            <span v-else>
              {{ item.contractInfo.inlineRates[0] ? `${formatMoney(item.contractInfo.inlineRates[0])}/t` : $t('notAvailable') }}
            </span>
          </template>
          <template #item.contractInfo.status="{item}">
            <v-chip
            small
            @click.stop="toggleSettlementStatus(item.contractInfo)"
            :class="getSettlementStatus(item.contractInfo.contractId).color"
            id="settlement-status-chip">
              {{getSettlementStatus(item.contractInfo.contractId).text}}
            </v-chip>
          </template>
          <template #expanded-item="{item}">
            <td :colspan="headers.length + 1" id="sbc-row-expanded">
              <SettlementAccountTable :contractInfo="item.contractInfo" :payeeInfo="item.payeeInfo" @refresh-settlement="refreshModuleDataAndClearStaleData"/>
            </td>
          </template>
          <template #item.actions="{item}">
            <SettlementsContractActions
              :contract="item.contractInfo"
              :tickets="item.payeeInfo[0].loadSummaries"
              @unposted="refreshModuleData()"
              @edited="refreshModuleData()"
            />
          </template>
        </v-data-table>
      </v-col>
      <Dialog :stateId="dialogId"
      @dialog-closing="closeRateVerification">
        <RateVerificationDialog
        v-if="rateVerificationDialog"
        @close="closeRateVerification"
        @refresh-batch="refreshModuleData()"
        :contractId="rateVerificationContractId"
        :contractMode="ContractMode.Logs"/>
      </Dialog>
    </v-row>
  </v-container>
</template>

<script>
import { formatMoney, numberWithCommas, mbfStringFromBf } from '@/utils/NumericMutations.js'
import { ContractMode, PayPeriodStatus, UserClaims } from '@/utils/Enumerations.js'
import { settlementsByContractHeaders } from '@/headers/Cycles.js'
import { mapActions, mapGetters } from 'vuex'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
import { uniqueDialogId, dataTableItemsPerPage, getDataTableDefaultItemsPerPage } from '@/utils/componentHelpers'
import { contractHasNegativePayment } from '@/components/settlements/NegativePaymentHelpers.js'

export default {
  name: 'SettlementsByContract',

  components: {
    SettlementAccountTable: () => import('@/components/settlements/accounts-payable-module/settlements-by-contract/SettlementAccountTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    RateVerificationDialog: () => import('@/components/settlements/rate-verification/RateVerificationDialog.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    SettlementsContractActions: () => import('@/components/settlements/SettlementsContractActions.vue')
  },

  props: {
    settlementsObject: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    dataTableItemsPerPage,
    expanded: [],
    rateVerificationDialog: false,
    rateVerificationSettlement: undefined,
    rateVerificationContractId: undefined,
    UserClaims,
    ContractMode,
    dialogId: uniqueDialogId('rate-verification-dialog')
  }),

  computed: {
    ...mapGetters('settlements', ['dateConfiguration', 'settlementContractStatuses']),
    ...mapGetters('user', ['companyInfo']),
    settlements () {
      if (this.settlementsObject === undefined) { return [] }
      return this.settlementsObject.settlements
    },

    hasVolumeData () {
      return this.settlements.some(s => s.contractInfo.netVolume > 0 || s.contractInfo.grossVolume > 0)
    },

    headers () {
      const headers = settlementsByContractHeaders().getColumns()
        .filter(h => this.hasVolumeData || (h.value !== 'contractInfo.netVolume' && h.value !== 'contractInfo.grossVolume'))
      headers.forEach(h => h.setCellClass('sbc-table-cell'))
      return headers
    },

    isExportBatch () {
      return this.dateConfiguration.exportBatch?.exportBatchId !== undefined
    }
  },

  methods: {
    ...mapActions('settlements', ['toggleContractStatus', 'refreshModuleData', 'clearStaleSubModuleData']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    formatMoney,
    numberWithCommas,
    mbfStringFromBf,
    userAssignedClaim,
    getDataTableDefaultItemsPerPage,
    contractHasNegativePayment,

    rowClicked (item) {
      if (item.contractInfo.contractId === this.expanded[0]?.contractInfo.contractId) {
        this.expanded = []
        return
      }
      this.expanded = []
      this.$nextTick(() => {
        this.expanded = [item]
      })
    },

    toggleSettlementStatus (contract) {
      if (this.dateConfiguration?.payPeriod.status === 3) return
      this.toggleContractStatus(contract)
    },

    getSettlementStatus (contractId) {
      if (this.dateConfiguration.payPeriod.status === PayPeriodStatus.Exported.value || this.dateConfiguration.exportBatch?.exportBatchId !== undefined) {
        return {
          text: this.$t('verified'),
          color: 'success white--text'
        }
      }

      const status = this.settlementContractStatuses[this.dateConfiguration.payPeriod.payPeriodId][contractId]
      const isSameAmount = status.oldAmt === status.amt

      if (status.verified === 1) {
        return {
          text: isSameAmount ? this.$t('verified') : this.$t('modifiedOldAmount', { amount: formatMoney(status.oldAmt) }),
          color: isSameAmount ? 'success white--text' : 'warning white--text'
        }
      } else {
        return {
          text: this.$t('needsReview'),
          color: 'blue-grey lighten-2 white--text'
        }
      }
    },

    rateVerificationClicked (settlement) {
      this.rateVerificationContractId = settlement.contractInfo.contractId
      this.rateVerificationDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '70%' })
    },

    closeRateVerification () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.rateVerificationDialog = false
      this.rateVerificationContractId = undefined
    },

    async refreshModuleDataAndClearStaleData () {
      await this.refreshModuleData(false)
      this.clearStaleSubModuleData({ isByproduct: false })
    }
  }
}
</script>
